import {
  type ComponentProps,
  type FC,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type MouseEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { type Accept, type FileRejection, useDropzone } from "react-dropzone";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import { useToast } from "../../providers/use-toast";
import {
  cn,
  downloadFile,
  type FileUploadData,
  fileExtensionFromMimeType,
  formatFileSize,
  getFileIcon,
  parseFilename,
} from "../../utils";
import { Badge } from "../badge";
import { Button, type ButtonProps } from "../button";
import { Icon, type IconProps } from "../icon";
import { Spinner } from "../loading";
import { Skeleton } from "../skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "../tooltip";
import { FIELD_BORDERLESS_CLASS_NAME, FIELD_READ_ONLY_CLASS_NAME } from "./field.constants";
import { FieldBase, type FieldBaseProps } from "./field-base";
import { useField } from "./use-field";

export type FieldFileUploadProps = FieldBaseProps & {
  label?: string;
  icon?: IconProps["icon"];
  placeholder?: string;
  multiple?: boolean;
  accept?: Accept;
  maxFiles?: number;
  maxSize?: number;
  minSize?: number;
  droppable?: boolean;
  borderless?: boolean;
  asThumbnails?: boolean;
  inputProps?: ComponentProps<typeof Button>;
  onUploadFiles: (acceptedFiles: File[]) => Promise<FileUploadData[]>;
  onError?: (error: Error) => void;
  onRemoveFile?: (id: string) => Promise<void>;
  onUploadingChange?: (isUploading: boolean) => void;
  onFocus?: ComponentProps<typeof Button>["onFocus"];
  onBlur?: ComponentProps<typeof Button>["onBlur"];
  preventDropOnDocument?: boolean;
};

export const FieldFileUpload: FC<FieldFileUploadProps> = ({
  placeholder = "Drag or click to upload files",
  icon = "cloud_upload",
  onUploadFiles,
  onError,
  onRemoveFile,
  onUploadingChange,
  inputProps,
  disabled,
  readOnly,
  multiple,
  accept,
  maxFiles,
  maxSize,
  minSize,
  droppable = true,
  borderless,
  asThumbnails,
  onFocus,
  onBlur,
  preventDropOnDocument = true,
  ...props
}) => {
  const isFileDialogOpenRef = useRef(false);
  const { control, clearErrors, trigger, getValues } = useFormContext();
  const { toast } = useToast();
  const [fileUploadingCount, setFileUploadingCount] = useState(0);
  const { field, hasError } = useField({ name: props.name, disabled, inputProps });
  const { append, remove, replace } = useFieldArray({ control, name: props.name });
  const files = useWatch({ name: props.name, control }) as FileUploadData[];

  const hasFiles = useMemo(() => !!files?.length, [files]);
  const isUploadingFiles = useMemo(() => fileUploadingCount > 0, [fileUploadingCount]);
  const isDisabled = useMemo(() => field.disabled || isUploadingFiles, [field.disabled, isUploadingFiles]);

  const handleAcceptedFiles = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) {
        return;
      }

      setFileUploadingCount(acceptedFiles.length);
      onUploadingChange?.(true);

      try {
        const fileData = await onUploadFiles(acceptedFiles);
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        multiple ? append(fileData) : replace(fileData);
      } catch (error) {
        onError?.(error as Error);
        toast({
          title: `Error uploading file${acceptedFiles.length > 1 ? "s" : ""}`,
          description: error instanceof Error ? error.message : "Please try again",
        });
      } finally {
        setFileUploadingCount(0);
        onUploadingChange?.(false);
      }
    },
    [append, onError, onUploadFiles, onUploadingChange, toast, multiple, replace, setFileUploadingCount]
  );

  const handleRejectedFiles = useCallback(
    (fileRejections: FileRejection[]) => {
      const combinedErrors = new Set<string>();
      for (const fileRejection of fileRejections) {
        for (const error of fileRejection.errors) {
          combinedErrors.add(error.message);
        }
      }

      const title = `${fileRejections.length} file${fileRejections.length > 1 ? "s" : ""} rejected`;
      const description = combinedErrors.size > 0 ? `${[...combinedErrors].join(". ")}.` : "An unknown error occurred.";

      toast({ variant: "destructive", title, description });
    },
    [toast]
  );

  const handleDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      clearErrors(props.name);

      if (fileRejections.length > 0) {
        handleRejectedFiles(fileRejections);
      }

      await handleAcceptedFiles(acceptedFiles);
    },
    [handleAcceptedFiles, handleRejectedFiles, clearErrors, props.name]
  );

  const handleFocus: FieldFileUploadProps["onFocus"] = (event) => {
    onFocus?.(event);
  };

  const handleBlur: FieldFileUploadProps["onBlur"] = async (event) => {
    // Don't trigger blur if the file dialog is open
    if (isFileDialogOpenRef.current) {
      isFileDialogOpenRef.current = false;
      return;
    }

    await trigger(props.name);
    onBlur?.(event);
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept,
    maxFiles,
    maxSize,
    minSize,
    disabled: isDisabled,
    multiple,
    noClick: true,
    noDrag: !droppable,
    onDragEnter: undefined,
    onDragLeave: undefined,
    onDragOver: undefined,
    onDrop: handleDrop,
    preventDropOnDocument,
  });

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (isDisabled) {
        return;
      }

      event.stopPropagation();
      isFileDialogOpenRef.current = true;
      open?.();
    },
    [isDisabled, open]
  );

  const handleRemoveFile = useCallback(
    async (id: string) => {
      try {
        await onRemoveFile?.(id);
        // Find the current index by ID after the async operation completes
        // to avoid stale indices when multiple files are removed concurrently
        const currentFiles = getValues(props.name) as FileUploadData[];
        const currentIndex = currentFiles.findIndex((f) => f.id === id);
        if (currentIndex !== -1) {
          remove(currentIndex);
        }
      } catch (error) {
        onError?.(error as Error);
        toast({ title: "Error removing file", description: "Please try again" });
      }
    },
    [remove, onError, onRemoveFile, toast, getValues, props.name]
  );

  return (
    <FieldBase
      {...props}
      label={
        props.label && (
          <div className="flex items-center gap-1.5 justify-between">
            {props.label}

            <span className="flex-1" />

            {multiple && !borderless && !readOnly && (
              <AddFilesButton
                tabIndex={!hasFiles ? -1 : 0}
                disabled={isDisabled}
                onClick={handleClick}
                onFocus={handleFocus}
                onBlur={handleBlur}
                loading={isUploadingFiles}
              />
            )}
          </div>
        )
      }
    >
      <Button
        {...getRootProps()}
        {...inputProps}
        variant="outline"
        type="button"
        disabled={isDisabled}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onClick={handleClick}
        asChild
        className={cn(
          "min-w-[180px] overflow-y-auto p-0! block w-full h-auto font-normal text-sm text-muted-foreground gap-0 flex-col bg-background! items-center justify-center text-center disabled:border-input! disabled:opacity-50 disabled:cursor-not-allowed disabled:read-only:bg-accent disabled:read-only:text-muted-foreground",
          {
            "opacity-50 cursor-not-allowed border-input! pointer-events-none": isDisabled,
            "border-dashed border-foreground/50 filled": isDragActive && !isDisabled,
            "border-destructive": hasError && !isDisabled,
            [FIELD_BORDERLESS_CLASS_NAME]: borderless,
            [FIELD_READ_ONLY_CLASS_NAME]: readOnly,
          },
          inputProps?.className
        )}
      >
        <div>
          {!hasFiles && !isUploadingFiles && (
            <div className="flex flex-col min-w-0 text-wrap items-center justify-center text-center px-12! py-8!">
              <Icon icon={icon} className="text-xl!" /> {placeholder}
            </div>
          )}

          {(hasFiles || isUploadingFiles) && (
            <div className={cn("relative flex flex-col min-w-0 flex-1 w-full")}>
              <FileList className={cn({ "p-0": borderless })}>
                {files?.map((file) => (
                  <FileListItem
                    key={file.id}
                    file={file}
                    disabled={isDisabled}
                    readOnly={readOnly}
                    onRemove={handleRemoveFile}
                    asThumbnail={asThumbnails}
                  />
                ))}

                {fileUploadingCount > 0 &&
                  Array.from({ length: fileUploadingCount }).map((_, index) => (
                    <FileListItemSkeleton key={index} asThumbnail={asThumbnails} />
                  ))}
              </FileList>

              {multiple && !readOnly && (
                <div className={cn({ "border-t border-border sticky bottom-0 bg-background": !borderless })}>
                  <AddMoreFilesButton
                    loading={(!props.label || borderless) && isUploadingFiles}
                    onClick={handleClick}
                    disabled={isDisabled}
                  />
                </div>
              )}
            </div>
          )}

          <input name={props.name} {...(getInputProps() as InputHTMLAttributes<HTMLInputElement>)} />
        </div>
      </Button>
    </FieldBase>
  );
};

const AddFilesButton: FC<ButtonProps & { loading?: boolean }> = ({ loading, ...props }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        {...props}
        variant="ghost"
        type="button"
        display="icon"
        size="2xs"
        className={cn("text-lg! -mr-0.5", {
          "opacity-50 cursor-not-allowed pointer-events-none": props.disabled,
        })}
      >
        {loading ? <Spinner className="size-3" /> : <Icon icon="add" />}
      </Button>
    </TooltipTrigger>
    <TooltipContent>Add files</TooltipContent>
  </Tooltip>
);

const AddMoreFilesButton: FC<ButtonProps & { loading?: boolean }> = ({ loading, ...props }) => (
  <Button {...props} variant="ghost" type="button" size="sm" className={cn("w-full rounded-t-none", props.className)}>
    {loading ? (
      <>
        <Spinner className="size-3" /> Uploading files&hellip;
      </>
    ) : (
      <>
        <Icon icon="add" /> Add more files
      </>
    )}
  </Button>
);

export interface FileListProps extends HTMLAttributes<HTMLUListElement> {}

const FileList: FC<FileListProps> = ({ children, className, ...props }) => (
  <ul {...props} className={cn("@container/file-list flex flex-row flex-wrap min-w-0 p-0.75", className)}>
    {children}
  </ul>
);

export interface FileListItemProps {
  file: FileUploadData;
  removable?: boolean;
  onRemove: (id: string) => Promise<void>;
  asThumbnail?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
}

export const FileListItem: FC<FileListItemProps> = ({
  file,
  removable = true,
  onRemove,
  asThumbnail,
  disabled,
  readOnly,
}) => {
  const handleRemove = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (file) {
      await onRemove?.(file.id);
    }
  };

  const handleClick = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      await downloadFile(file);
    },
    [file]
  );

  const icon = useMemo(() => getFileIcon(file.mimeType), [file.mimeType]);

  return (
    <FileListItemWrapper asThumbnail={asThumbnail}>
      <Icon
        icon={icon}
        className={cn("order-0 size-3 text-sm text-foreground opacity-50 filled", {
          "hidden @sm/file-list:inline-block ml-0.5 mr-0.25": !asThumbnail,
        })}
      />

      <Button
        variant="unstyled"
        size="sm"
        onClick={handleClick}
        asChild
        className={cn("order-1 block text-xs", {
          "text-left flex-1 justify-start truncate": !asThumbnail,
          "max-w-full text-wrap! line-clamp-3 break-words": asThumbnail,
        })}
      >
        <span>{parseFilename(file.filename).name}</span>
      </Button>

      <span className="order-2 text-muted-foreground">{formatFileSize(file.size ?? 0)}</span>

      {asThumbnail && <span className="order-3 flex-1" />}

      <Badge
        variant="secondary"
        size="2xs"
        type="square"
        className={cn("order-4 uppercase", {
          "order-0 @sm/file-list:order-4": !asThumbnail,
          "mr-1": readOnly && !asThumbnail,
        })}
      >
        {fileExtensionFromMimeType(file.mimeType)}
      </Badge>

      {removable && !readOnly && (
        <Button
          variant="ghost"
          size="2xs"
          display="icon"
          type="button"
          onClick={handleRemove}
          disabled={disabled}
          className={cn("order-5", { "absolute top-1.5 right-1.5": asThumbnail })}
        >
          <Icon icon="close" />
        </Button>
      )}
    </FileListItemWrapper>
  );
};

export const FileListItemSkeleton: FC<FileListItemWrapperProps> = ({ asThumbnail }) => (
  <FileListItemWrapper asThumbnail={asThumbnail}>
    <Skeleton className={cn("bg-foreground/15 size-3 ml-0.25", { "mb-1 mx-0": asThumbnail })} />
    <Skeleton
      className={cn("bg-foreground/15 h-3 mx-0.5", { "flex-1": !asThumbnail, " h-4 mx-0 w-full": asThumbnail })}
    />
    <Skeleton className={cn("bg-foreground/15 w-6 h-3 mx-0.5", { "h-4 mx-0 mt-2": asThumbnail })} />
    <Skeleton className={cn("bg-foreground/15 size-3 mr-0.5", { "mx-0 absolute top-2.5 right-2.5": asThumbnail })} />
  </FileListItemWrapper>
);

export interface FileListItemWrapperProps extends HTMLAttributes<HTMLLIElement> {
  asThumbnail?: boolean;
}

export const FileListItemWrapper: FC<FileListItemWrapperProps> = ({
  children,
  asThumbnail,
  onClick,
  className,
  ...props
}) => {
  const handleClick = useCallback(
    (event: MouseEvent<HTMLLIElement>) => {
      event.preventDefault();
      event.stopPropagation();
      onClick?.(event);
    },
    [onClick]
  );

  return (
    <li
      {...props}
      onClick={handleClick}
      className={cn(
        "flex min-w-0 max-w-full cursor-default truncate",
        {
          "p-0.5 basis-full w-full shrink-0": !asThumbnail,
          "p-0.75 shrink-1 basis-full @xs/file-list:basis-1/2 @sm/file-list:basis-1/3 @lg/file-list:basis-1/4":
            asThumbnail,
        },
        className
      )}
    >
      <div
        className={cn(
          "relative h-8 truncate text-xs text-left flex flex-1 items-center min-w-0 w-full gap-1.5 bg-popover border border-input text-foreground pl-2 pr-1.5 py-2 rounded-md",
          { "flex-col items-start h-auto p-3": asThumbnail }
        )}
      >
        {children}
      </div>
    </li>
  );
};
